import {
  BookOpen,
  Settings2,
  Users,
  FileBarChart,
  Calculator,
  Shield,
  Receipt,
  Sparkles,
  ChevronDown,
  X,
  Landmark,
  FileCheck2,
  Scale,
  Wallet,
} from "lucide-react";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

export const TDS_GUIDE_SECTIONS = [
  {
    id: "overview",
    icon: Receipt,
    title: "What is TDS?",
    content: [
      "TDS (Tax Deducted at Source) is income tax deducted by the employer from employee salary every month and paid to the Income Tax Department.",
      "During payroll: Gross Salary (−) PF (−) ESI (−) Professional Tax (−) TDS = Net Salary.",
    ],
    example: "Employee annual tax = ₹1,20,000 → Monthly TDS = ₹1,20,000 ÷ 12 = ₹10,000",
  },
  {
    id: "tax-regime",
    icon: Landmark,
    title: "Tax Regime",
    content: [
      "Old Regime: Higher slab rates but allows HRA, LTA, 80C, 80D, and other deductions.",
      "New Regime: Lower slab rates with simplified rules and limited deductions.",
      "Employees choose a regime at the start of the year. HR configures which regimes are enabled.",
    ],
    example: "Choose Old if employees claim rent receipts and investments; choose New for simpler processing.",
  },
  {
    id: "configuration",
    icon: Settings2,
    title: "TDS Configuration",
    content: [
      "One-time organization setup used by payroll, TDS calculation, Form 16, and reports.",
      "Deductor Information: TAN, company name, address, state (mandatory fields).",
      "Calculation: Annualized projection, previous employer income, bonus, auto-recalculate.",
      "Form 16: Authorized signatory, designation, place, digital signature.",
    ],
  },
  {
    id: "employee-tax",
    icon: Users,
    title: "Employee Tax Setup",
    content: [
      "Review employee tax registers, regime, declaration status, and proof status.",
      "Set Declaration Window and Proof Submission Window dates to control employee access.",
    ],
    example: "80C max ₹1,50,000 | 80D max ₹25,000",
  },
  {
    id: "investment-declaration",
    icon: Wallet,
    title: "Investment Declaration",
    content: [
      "Employees declare planned investments (80C, 80D, etc.) at the start of the year.",
      "HR reviews declarations before payroll uses them for TDS projection.",
      "Applicable mainly under the Old Tax Regime.",
    ],
  },
  {
    id: "proof-verification",
    icon: FileCheck2,
    title: "Proof Verification",
    content: [
      "After year-end, employees upload proof documents for declared investments.",
      "HR verifies proofs and approves or rejects with remarks.",
      "Approved proofs finalize tax computation for Form 16.",
    ],
  },
  {
    id: "exemptions",
    icon: Shield,
    title: "Exemptions",
    content: [
      "HRA, LTA, and other exemptions reduce taxable income under the Old Regime.",
      "Configure exemption rules in employee tax setup before running payroll.",
    ],
  },
  {
    id: "computation",
    icon: Calculator,
    title: "Tax Computation",
    content: [
      "Gross Income (−) Exemptions = Taxable Income",
      "Apply tax slabs based on selected regime → Annual Tax",
      "Monthly TDS = Annual Tax ÷ Remaining Months in FY",
    ],
    example: "Gross ₹15,00,000 − 80C ₹1,50,000 = Taxable ₹13,50,000",
  },
  {
    id: "monthly-tds",
    icon: FileBarChart,
    title: "Monthly TDS Register",
    content: [
      "Shows month-wise TDS deducted per employee during payroll.",
      "Use this register to verify deductions before depositing tax with the government.",
    ],
  },
  {
    id: "reports",
    icon: FileBarChart,
    title: "TDS Reports",
    content: [
      "Deduction register, tax projection, and summary reports for HR and finance.",
      "Export to Excel or PDF for audit and management review.",
    ],
  },
  {
    id: "form16",
    icon: FileBarChart,
    title: "Form 16",
    content: [
      "Annual tax certificate issued to employees after year-end.",
      "Part A shows TDS deposited; Part B shows salary and tax computation.",
      "Generate after reconciliation is complete.",
    ],
  },
  {
    id: "challan-management",
    icon: Landmark,
    title: "Challan Management",
    content: [
      "Record tax deposited with the government using challan number, BSR code, and date.",
      "Map challan amounts to employees for statutory filing.",
    ],
  },
  {
    id: "quarterly-return",
    icon: Receipt,
    title: "Quarterly Return",
    content: [
      "Form 24Q is filed every quarter with employee-wise TDS details.",
      "Prepare return data from payroll, validate, and upload to the TRACES portal.",
    ],
  },
  {
    id: "reconciliation",
    icon: Scale,
    title: "Reconciliation",
    content: [
      "Match TDS deducted from payroll with TDS deposited via challans.",
      "Resolve variances before generating Form 16 or filing returns.",
    ],
  },
  {
    id: "roles",
    icon: Shield,
    title: "User Roles",
    content: [
      "Payroll Admin: Configure TDS, approve declarations, generate Form 16, view reports.",
      "Employee: Select tax regime, submit declaration, upload proofs, download Form 16.",
    ],
  },
  {
    id: "payroll",
    icon: Sparkles,
    title: "Payroll Integration",
    content: [
      "TDS is deducted automatically during every payroll run when Auto Recalculate TDS is enabled.",
      "Formula: Gross Earnings (−) PF (−) ESI (−) PT (−) TDS = Net Salary.",
    ],
    example: "Gross ₹1,00,000 − PF ₹1,800 − PT ₹200 − TDS ₹10,000 = Net ₹88,000",
  },
];

function filterGuideForRegime(sections, regime) {
  if (regime !== "new") return sections;
  const oldOnly = new Set(["investment-declaration", "proof-verification", "exemptions"]);
  return sections.filter((s) => !oldOnly.has(s.id));
}

function GuideAccordion({ sections, defaultOpen = "overview" }) {
  const [openId, setOpenId] = useState(defaultOpen);

  return (
    <div className="space-y-3">
      {sections.map((sec) => {
        const Icon = sec.icon;
        const open = openId === sec.id;
        return (
          <div key={sec.id} className="tds-guide-section">
            <button
              type="button"
              className="tds-guide-section-head"
              onClick={() => setOpenId(open ? null : sec.id)}
            >
              <span className="flex items-center gap-2">
                {Icon ? <Icon size={16} className="text-violet-600" /> : null}
                {sec.title}
              </span>
              <ChevronDown
                size={16}
                className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
              />
            </button>
            <AnimatePresence initial={false}>
              {open && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.22 }}
                  className="overflow-hidden"
                >
                  <div className="tds-guide-section-body">
                    <ul>
                      {sec.content.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                    {sec.example ? (
                      <div className="tds-guide-tip">
                        Example: {sec.example}
                      </div>
                    ) : null}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

export function TdsGuideSidebar({ defaultSection = "overview", regime = "new" }) {
  const sections = filterGuideForRegime(TDS_GUIDE_SECTIONS, regime);
  return (
    <div className="tds-guide-sidebar">
      <div className="tds-guide-sidebar__head">
        <BookOpen size={16} />
        <div>
          <h3 className="tds-guide-sidebar__title">User Guide</h3>
          <p className="tds-guide-sidebar__sub">Simple explanations for each step</p>
        </div>
      </div>
      <GuideAccordion sections={sections} defaultOpen={defaultSection} />
    </div>
  );
}

export function TdsUserGuidePanel({ open, onClose, initialSection = "overview", regime }) {
  const sections = regime ? filterGuideForRegime(TDS_GUIDE_SECTIONS, regime) : TDS_GUIDE_SECTIONS;
  return (
    <AnimatePresence>
      {open && (
    <motion.div
      className="tds-guide-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="tds-guide-panel"
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 28, stiffness: 320 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tds-guide-panel-head">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="tds-hero-eyebrow">
                <BookOpen size={10} />
                User Guide
              </div>
              <h2 className="text-lg font-bold mt-1">TDS Module Guide</h2>
              <p className="text-sm opacity-80 mt-1">
                Everything you need to configure, manage, and comply with TDS.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              aria-label="Close guide"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="tds-guide-panel-body">
          <GuideAccordion sections={sections} defaultOpen={initialSection} />
        </div>
      </motion.div>
    </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function TdsUserGuidePage() {
  return (
    <div className="tds-page-content max-w-5xl mx-auto">
      <div className="tds-guide-page-hero">
        <div className="relative z-10">
          <div className="tds-hero-eyebrow inline-flex mb-4">
            <BookOpen size={10} />
            TDS User Guide
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">
            Master TDS Configuration & Compliance
          </h1>
          <p className="mt-3 text-sm md:text-base opacity-85 max-w-2xl mx-auto">
            A complete walkthrough of Tax Deducted at Source — from organization setup
            to employee declarations, payroll integration, and statutory reporting.
          </p>
        </div>
      </div>

      <div className="tds-guide-cards mb-8">
        {TDS_GUIDE_SECTIONS.map((sec) => {
          const Icon = sec.icon;
          return (
            <motion.div
              key={sec.id}
              className="tds-guide-card"
              whileHover={{ y: -3 }}
              transition={{ duration: 0.2 }}
            >
              <div className="tds-guide-card-icon">
                <Icon size={20} />
              </div>
              <h3 className="font-bold text-slate-900 text-sm">{sec.title}</h3>
              <ul className="mt-2 space-y-1.5 text-xs text-slate-600">
                {sec.content.slice(0, 2).map((line) => (
                  <li key={line} className="line-clamp-2">{line}</li>
                ))}
              </ul>
              {sec.example ? (
                <p className="mt-3 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
                  {sec.example}
                </p>
              ) : null}
            </motion.div>
          );
        })}
      </div>

      <div className="tds-panel">
        <div className="tds-panel-head">
          <div>
            <h2 className="tds-panel-title">Detailed Reference</h2>
            <p className="tds-panel-sub">Expand each section for full documentation</p>
          </div>
        </div>
        <div className="tds-panel-body">
          <GuideAccordion sections={TDS_GUIDE_SECTIONS} defaultOpen={null} />
        </div>
      </div>
    </div>
  );
}
